import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { trackNav } from "../lib/navTrack";
import { API } from "../lib/api";

// Public (logged-out) primary navigation — six categories, per-category neon
// accents, caret-free terminal-prompt indicators (design_guidelines.json).
// Desktop ≥1200px inline · Tablet 768–1199px segmented second row · Phone drawer.

const ACCENTS = {
    learn:     { c: "#22d3ee", tint: "rgba(34,211,238,0.08)",  glow: "rgba(34,211,238,0.22)" },
    practice:  { c: "#4ade80", tint: "rgba(74,222,128,0.08)",  glow: "rgba(74,222,128,0.22)" },
    compete:   { c: "#a855f7", tint: "rgba(168,85,247,0.08)",  glow: "rgba(168,85,247,0.22)" },
    hunt:      { c: "#fbbf24", tint: "rgba(251,191,36,0.08)",  glow: "rgba(251,191,36,0.22)" },
    community: { c: "#38bdf8", tint: "rgba(56,189,248,0.08)",  glow: "rgba(56,189,248,0.22)" },
    company:   { c: "#f43f5e", tint: "rgba(244,63,94,0.08)",   glow: "rgba(244,63,94,0.22)" },
};

export const NAV_CATEGORIES = [
    {
        key: "learn", label: "Learn",
        links: [
            ["Academy", "/learn", "guided hands-on hacking modules"],
            ["Career Tracks", "/tracks", "structured offensive & defensive paths"],
            ["Certifications", "/certifications", "practical, verifiable certs"],
            ["Skill Explorer", "/explorer", "map & test your skill matrix"],
            ["Topic Hubs", "/topics", "every topic: module + labs + writeups"],
        ],
    },
    {
        key: "practice", label: "Practice",
        links: [
            ["Vulnerable Labs", "/labs", "real-world target machines"],
            ["Attack Box", "/box", "kali linux in your browser"],
            ["Network Labs", "/network-labs", "multi-host enterprise networks"],
            ["Investigations", "/investigations", "forensics & incident scenarios"],
            ["Payload Tools", "/tools", "encoders, shells, generators"],
        ],
    },
    {
        key: "compete", label: "Compete",
        links: [
            ["CTF Arena", "/ctf", "24/7 capture-the-flag challenges"],
            ["Multiplayer", "/multiplayer", "head-to-head speed hacking"],
            ["Tournaments", "/tournaments", "scheduled championships"],
            ["Events", "/events", "conferences & hackathons"],
            ["Leaderboard", "/leaderboard", "global hunter rankings"],
            ["Teams", "/teams", "competitive hacking squads"],
        ],
    },
    {
        key: "hunt", label: "Hunt",
        links: [
            ["Bug Bounty Programs", "/programs", "900+ public program directory"],
            ["Security Jobs", "/jobs", "offensive & secops career board"],
        ],
    },
    {
        key: "community", label: "Community",
        links: [
            ["Communities", "/communities", "subforums & interest hubs"],
            ["Q&A", "/q", "technical peer q&a and writeups"],
            ["News", "/news", "latest cves, research & advisories"],
            ["FAQ", "/faq", "platform guides & help center"],
        ],
    },
    {
        key: "company", label: "Company",
        groups: [
            ["About & product", [["About", "/about"], ["Pricing", "/pricing"], ["Roadmap", "/roadmap"], ["Changelog", "/changelog"]]],
            ["Trust & support", [["Security", "/security"], ["Contact", "/contact"]]],
            ["Media", [["Press Kit", "/press"], ["Brand Assets", "/brand"]]],
        ],
    },
];

function catLinks(cat) {
    return cat.links || cat.groups.flatMap(([, ls]) => ls);
}

function useActiveCategory() {
    const { pathname } = useLocation();
    let best = null, bestLen = 0;
    for (const cat of NAV_CATEGORIES) {
        for (const [, to] of catLinks(cat)) {
            if ((pathname === to || pathname.startsWith(`${to}/`) || pathname.startsWith(`${to}?`)) && to.length > bestLen) {
                best = cat.key; bestLen = to.length;
            }
        }
    }
    return best;
}

function DropdownPanel({ cat, onSelect, alignRight }) {
    const acc = ACCENTS[cat.key];
    return (
        <div id={`nav-panel-${cat.key}`} data-testid={`nav-panel-${cat.key}`}
             style={{ "--acc": acc.c, borderColor: acc.glow }}
             className={`absolute top-full mt-2.5 ${alignRight ? "right-0" : "left-0"} ${cat.groups ? "w-[520px]" : "w-[300px]"} max-w-[calc(100vw-1.5rem)]
                 bg-[#0a0f1a]/95 backdrop-blur-xl border rounded-md p-2 z-50 shadow-xl shadow-black/50
                 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1 motion-safe:duration-150`}>
            <div className="h-[2px] rounded-full mb-1.5 mx-1" style={{ background: `linear-gradient(90deg, ${acc.c}, transparent)` }} />
            {cat.groups ? (
                <div className="grid grid-cols-3 gap-x-2 pb-1">
                    {cat.groups.map(([gLabel, gLinks]) => (
                        <div key={gLabel}>
                            <div className="text-[9px] uppercase tracking-widest font-mono text-slate-500 px-2.5 pt-1.5 pb-1">// {gLabel}</div>
                            <ul>
                                {gLinks.map(([name, to], i) => (
                                    <li key={to}>
                                        <Link to={to} onClick={() => { trackNav("click", cat.key, to); onSelect(); }} data-testid={`nav-link${to.replace(/\//g, "-")}`}
                                              className="group flex items-baseline gap-1.5 px-2.5 py-2 text-[13px] text-slate-300 hover:text-white border-l-2 border-transparent hover:border-[color:var(--acc)] hover:bg-white/[0.04] rounded-r-sm transition-colors motion-reduce:transition-none">
                                            <span className="font-mono text-[10px] font-bold opacity-50 group-hover:opacity-100 text-[color:var(--acc)]" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                                            {name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            ) : (
                <ul className="pb-0.5">
                    {cat.links.map(([name, to, desc], i) => (
                        <li key={to}>
                            <Link to={to} onClick={() => { trackNav("click", cat.key, to); onSelect(); }} data-testid={`nav-link${to.replace(/\//g, "-")}`}
                                  className="group flex items-start gap-2.5 px-2.5 py-2 border-l-2 border-transparent hover:border-[color:var(--acc)] hover:bg-white/[0.04] rounded-r-sm transition-colors motion-reduce:transition-none">
                                <span className="font-mono text-[10px] font-bold mt-[3px] opacity-50 group-hover:opacity-100 text-[color:var(--acc)] transition-opacity" aria-hidden="true">
                                    {String(i + 1).padStart(2, "0")} <span className="opacity-60">//</span>
                                </span>
                                <span className="min-w-0">
                                    <span className="block text-[13px] font-medium text-slate-200 group-hover:text-white leading-tight">{name}</span>
                                    <span className="block text-[10.5px] font-mono text-slate-500 group-hover:text-slate-400 mt-0.5 truncate">{desc}</span>
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function Trigger({ cat, open, isActive, onToggle, refFn, grow }) {
    const acc = ACCENTS[cat.key];
    return (
        <div className={`relative ${grow ? "flex-1 flex justify-center" : ""}`}>
            <button
                ref={refFn}
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={`nav-panel-${cat.key}`}
                data-testid={`nav-cat-${cat.key}`}
                style={{
                    "--acc": acc.c,
                    ...(open ? { backgroundColor: acc.tint } : {}),
                }}
                className={`relative group flex items-center gap-1.5 h-11 px-3 font-mono text-[12px] font-semibold tracking-wider uppercase whitespace-nowrap rounded-sm
                    transition-colors motion-reduce:transition-none
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black
                    ${open || isActive ? "text-[color:var(--acc)]" : "text-slate-300 hover:text-white"}`}
            >
                {isActive && (
                    <span className="w-1.5 h-1.5 rounded-full motion-safe:animate-pulse shrink-0" style={{ backgroundColor: acc.c }} aria-hidden="true" />
                )}
                <span aria-hidden="true"
                      className={`font-bold text-[color:var(--acc)] transition-opacity motion-reduce:transition-none ${open ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                    {open ? "[" : ">"}
                </span>
                {cat.label}
                <span aria-hidden="true" className={`font-bold text-[color:var(--acc)] ${open ? "opacity-100" : "opacity-0"}`}>]</span>
                {isActive && <span className="sr-only">(current section)</span>}
                <span aria-hidden="true"
                      className={`absolute bottom-0.5 left-3 right-3 h-[2px] rounded-full origin-left transition-transform motion-reduce:transition-none
                          ${open || isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`}
                      style={{ backgroundColor: acc.c }} />
            </button>
            {open && <DropdownPanel cat={cat} onSelect={() => onToggle(true)} alignRight={NAV_CATEGORIES.findIndex((c) => c.key === cat.key) >= 4} />}
        </div>
    );
}

function CategoryBar({ testid, className, grow = false, surface = "desktop" }) {
    const [openKey, setOpenKey] = useState(null);
    const active = useActiveCategory();
    const rootRef = useRef(null);
    const triggerRefs = useRef({});

    useEffect(() => {
        if (!openKey) return undefined;
        const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpenKey(null); };
        const onKey = (e) => {
            if (e.key === "Escape") {
                triggerRefs.current[openKey]?.focus();
                setOpenKey(null);
            }
        };
        document.addEventListener("mousedown", onDoc);
        window.addEventListener("keydown", onKey);
        return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("keydown", onKey); };
    }, [openKey]);

    return (
        <nav ref={rootRef} aria-label="Primary navigation" data-testid={testid} className={className}>
            {NAV_CATEGORIES.map((cat) => (
                <Trigger key={cat.key} cat={cat} grow={grow}
                         open={openKey === cat.key}
                         isActive={active === cat.key}
                         refFn={(el) => { triggerRefs.current[cat.key] = el; }}
                         onToggle={(forceClose) => {
                             const willOpen = !(forceClose === true || openKey === cat.key);
                             if (willOpen) trackNav("open", cat.key, "", surface);
                             setOpenKey(willOpen ? cat.key : null);
                         }} />
            ))}
        </nav>
    );
}

// ── Desktop ≥1200px: centered in the main header row ────────────────────────
export function PublicNavDesktop() {
    return (
        <div className="hidden min-[1200px]:flex flex-1 justify-center min-w-0">
            <CategoryBar testid="public-nav-desktop" className="flex items-center gap-0.5" />
        </div>
    );
}

// ── Tablet 768–1199px: segmented second header row ──────────────────────────
export function PublicNavTabletRow() {
    return (
        <div className="hidden md:block min-[1200px]:hidden border-t border-white/[0.06] bg-[#070b14]">
            <CategoryBar grow testid="public-nav-tablet" surface="tablet"
                         className="w-full px-2 sm:px-4 flex items-stretch h-12 divide-x divide-white/[0.05]" />
        </div>
    );
}

// ── Phone <768px: labelled Menu button + terminal accordion drawer ──────────
export function PublicNavMobile() {
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [live, setLive] = useState(null);
    const active = useActiveCategory();
    const btnRef = useRef(null);

    useEffect(() => {
        if (!open || live) return;
        Promise.all([
            fetch(`${API}/landing/stats`).then((r) => r.json()),
            fetch(`${API}/stats/online-now`).then((r) => r.json()),
        ]).then(([s, o]) => setLive({ labs: s.labs_total, online: o.count })).catch(() => {});
    }, [open, live]);

    const close = useCallback(() => {
        setOpen(false);
        btnRef.current?.focus();
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        setExpanded(active);
        const onKey = (e) => { if (e.key === "Escape") close(); };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <div className="md:hidden">
            <button ref={btnRef} onClick={() => setOpen((v) => !v)}
                    aria-expanded={open} aria-controls="public-nav-drawer" aria-label="Site menu"
                    data-testid="public-nav-menu-btn"
                    className={`relative flex flex-col items-start justify-center gap-[5px] h-11 w-10 min-[360px]:w-11 px-[10px] min-[360px]:px-[11px] rounded-sm border transition-colors motion-reduce:transition-none
                        ${open ? "text-cyan-300 border-cyan-500/50 bg-cyan-500/10" : "text-slate-200 border-white/10 hover:text-cyan-300 hover:border-cyan-500/30"}`}>
                <span aria-hidden="true" className={`block h-[2px] w-[18px] rounded-full bg-current transition-transform motion-reduce:transition-none ${open ? "translate-y-[7px] rotate-45" : ""}`} />
                <span aria-hidden="true" className={`block h-[2px] w-[13px] rounded-full bg-current transition-opacity motion-reduce:transition-none ${open ? "opacity-0" : ""}`} />
                <span aria-hidden="true" className={`block h-[2px] rounded-full bg-current transition-all motion-reduce:transition-none ${open ? "w-[18px] -translate-y-[7px] -rotate-45" : "w-[8px] hc-cursor-blink"}`} />
            </button>
            {open && createPortal(
                <>
                    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]" data-testid="public-nav-backdrop" onClick={close} aria-hidden="true" />
                    <nav id="public-nav-drawer" aria-label="Primary navigation" data-testid="public-nav-drawer"
                         className="fixed inset-0 w-full bg-[#070b14]/[0.99] backdrop-blur-2xl shadow-2xl shadow-black/80 z-[70] flex flex-col
                             motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-right motion-safe:duration-200"
                         style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}>
                        <div className="flex items-center justify-between px-4 h-14 border-b border-white/[0.07] shrink-0">
                            <div className="flex items-center gap-2">
                                <span className="w-7 h-7 rounded-md bg-cyan-400 flex items-center justify-center font-mono text-[13px] font-black text-slate-950" aria-hidden="true">&gt;_</span>
                                <div className="leading-tight">
                                    <div className="font-bold text-[13px] text-slate-100" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-notranslate>hacking<span className="text-cyan-400">.</span>community</div>
                                    <div className="font-mono text-[8px] uppercase tracking-[0.3em] text-slate-600">~/navigate</div>
                                </div>
                            </div>
                            <button onClick={close} aria-label="Close menu" data-testid="public-nav-drawer-close"
                                    className="font-mono text-[12px] font-bold text-slate-400 hover:text-cyan-300 border border-white/10 hover:border-cyan-500/40 rounded-sm h-8 px-2 transition-colors">
                                [x]
                            </button>
                        </div>
                        <div className="h-[2px] shrink-0" style={{ background: "linear-gradient(90deg, #22d3ee, #a855f7, transparent)" }} aria-hidden="true" />
                        {live && (
                            <div className="flex gap-2 px-3 py-2.5 border-b border-white/[0.06] shrink-0" data-testid="drawer-live-stats">
                                <div className="flex-1 flex items-center gap-2 h-10 px-3 rounded-sm border border-cyan-500/25 bg-cyan-500/[0.06]">
                                    <span className="font-mono text-[15px] font-black text-cyan-300">{live.labs}</span>
                                    <span className="font-mono text-[9px] uppercase tracking-widest text-slate-400">labs to root</span>
                                </div>
                                <div className="flex-1 flex items-center gap-2 h-10 px-3 rounded-sm border border-emerald-500/25 bg-emerald-500/[0.06]">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 motion-safe:animate-pulse" aria-hidden="true" />
                                    <span className="font-mono text-[15px] font-black text-emerald-300">{live.online}</span>
                                    <span className="font-mono text-[9px] uppercase tracking-widest text-slate-400">hackers online</span>
                                </div>
                            </div>
                        )}
                        <div className="flex-1 overflow-y-auto p-2">
                        <ul>
                            {NAV_CATEGORIES.map((cat) => {
                                const acc = ACCENTS[cat.key];
                                const isOpen = expanded === cat.key;
                                const isActive = active === cat.key;
                                return (
                                    <li key={cat.key} className="border-b border-white/[0.05] last:border-0"
                                        style={isOpen ? { borderLeft: `2px solid ${acc.c}`, background: acc.tint } : { borderLeft: "2px solid transparent" }}>
                                        <button onClick={() => { if (!isOpen) trackNav("open", cat.key, "", "mobile"); setExpanded(isOpen ? null : cat.key); }}
                                                aria-expanded={isOpen} aria-controls={`drawer-panel-${cat.key}`}
                                                data-testid={`drawer-cat-${cat.key}`}
                                                style={{ "--acc": acc.c }}
                                                className={`w-full flex items-center gap-2 h-12 px-3 font-mono text-[12px] font-semibold uppercase tracking-widest rounded-sm transition-colors motion-reduce:transition-none
                                                    ${isOpen || isActive ? "text-[color:var(--acc)]" : "text-slate-200"}`}>
                                            <span aria-hidden="true" className="font-bold text-[color:var(--acc)]">{isOpen ? "v" : ">"}</span>
                                            {cat.label}
                                            {isActive && <span className="w-1.5 h-1.5 rounded-full motion-safe:animate-pulse ml-1" style={{ backgroundColor: acc.c }} aria-hidden="true" />}
                                            <span className="ml-auto font-mono text-[9px] text-slate-600">{String(catLinks(cat).length).padStart(2, "0")}</span>
                                        </button>
                                        {isOpen && (
                                            <ul id={`drawer-panel-${cat.key}`} className="pb-2">
                                                {catLinks(cat).map(([name, to], i) => (
                                                    <li key={to}>
                                                        <Link to={to} onClick={() => { trackNav("click", cat.key, to, "mobile"); setOpen(false); }}
                                                              data-testid={`drawer-link${to.replace(/\//g, "-")}`}
                                                              className="flex items-center gap-2.5 min-h-[44px] px-5 text-[13.5px] text-slate-300 hover:text-white hover:bg-white/5 rounded-sm transition-colors motion-reduce:transition-none">
                                                            <span className="font-mono text-[10px] font-bold opacity-60" style={{ color: acc.c }} aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                                                            {name}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                        </div>
                        <div className="shrink-0 border-t border-white/[0.07] p-2">
                        <div className="flex gap-2">
                        <Link to="/login" onClick={() => setOpen(false)} data-testid="drawer-signin-link"
                              className="flex-1 flex items-center justify-center h-11 border border-slate-600 hover:border-cyan-500/50 text-slate-100 font-mono font-bold rounded-sm text-xs uppercase tracking-wider transition-colors motion-reduce:transition-none">
                            Sign in
                        </Link>
                        <Link to="/login" onClick={() => setOpen(false)} data-testid="drawer-join-link"
                              className="flex-1 flex items-center justify-center gap-1.5 h-11 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold rounded-sm text-xs font-mono uppercase tracking-wider transition-colors motion-reduce:transition-none">
                            &gt;_ Join free
                        </Link>
                        </div>
                        <div className="pt-2 px-1 font-mono text-[8.5px] text-slate-600 flex justify-between">
                            <span>// free to start · no card</span>
                            <span>esc closes</span>
                        </div>
                        </div>
                    </nav>
                </>,
                document.body
            )}
        </div>
    );
}
